import { supabase, supabaseUrl, supabaseAnonKey } from './supabase';
import { Booking } from '../types';

export async function createOrder(booking: Booking): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('Creating order:', booking);

    // Validate required fields
    if (!booking.order_number || !booking.customer_name || !booking.customer_phone || !booking.product) {
      throw new Error('Missing required order fields');
    }

    const { error } = await supabase
      .from('orders')
      .insert({
        order_number: booking.order_number,
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
        customer_phone: booking.customer_phone,
        product: booking.product,
        delivery_address: booking.delivery_address,
        installation_location: booking.installation_location,
        has_trade: booking.has_trade,
        requires_haul_away: booking.requires_haul_away,
        notes: booking.notes,
        status: booking.status,
        payment_method: booking.payment_method,
        delivery_schedule: booking.delivery_schedule,
        status_history: booking.status_history,
        refund: booking.refund,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Supabase error creating order:', error);
      throw new Error(`Failed to create order: ${error.message}`);
    }

    console.log('Order created successfully');
    return { success: true };
  } catch (error) {
    console.error('Error creating order:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to create order' 
    };
  }
}

export async function getOrders(): Promise<Booking[]> {
  try {
    console.log('Fetching orders...');
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Supabase error fetching orders:', error);
      throw new Error(`Failed to fetch orders: ${error.message}`);
    }

    console.log('Orders fetched successfully:', data?.length || 0, 'orders');
    return data || [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
}

export async function getOrderByNumber(orderNumber: string): Promise<Booking | null> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('order_number', orderNumber)
      .single();

    if (error) {
      console.error('Supabase error fetching order:', error);
      throw new Error(`Failed to fetch order: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
}

export async function updateOrder(order: Booking): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('Updating order via server route:', {
      id: order.id,
      status: order.status,
      timestamp: new Date().toISOString()
    });

    // Call the secure server route with service role
    const response = await fetch(
      `${supabaseUrl}/functions/v1/update-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          id: order.id,
          updates: {
            status: order.status,
            delivery_schedule: order.delivery_schedule,
            status_history: order.status_history,
            refund: order.refund,
            updated_at: new Date().toISOString()
          }
        })
      }
    );

    console.log('Server response status:', response.status);

    if (!response.ok) {
      const rawText = await response.text().catch(() => '');
      let errorMsg = `Update failed with status ${response.status}`;
      try {
        const errorData = JSON.parse(rawText);
        errorMsg = errorData.error || errorData.message || errorMsg;
      } catch {
        if (rawText) errorMsg = rawText.slice(0, 200);
      }
      console.error('Server update failed:', response.status, errorMsg);
      throw new Error(errorMsg);
    }

    const result = await response.json();
    console.log('Order update successful:', result);

    return { success: true };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('Error updating order:', msg);
    return {
      success: false,
      error: msg || 'Failed to update order'
    };
  }
}

export async function deleteOrder(orderId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('Deleting order via server route:', orderId);

    // Call the secure server route with service role
    const response = await fetch(
      `${supabaseUrl}/functions/v1/delete-order`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({ id: orderId })
      }
    );

    console.log('Server response status:', response.status);

    if (!response.ok) {
      const rawText = await response.text().catch(() => '');
      let errorMsg = `Deletion failed with status ${response.status}`;
      try {
        const errorData = JSON.parse(rawText);
        errorMsg = errorData.error || errorData.message || errorMsg;
      } catch {
        if (rawText) errorMsg = rawText.slice(0, 200);
      }
      console.error('Server deletion failed:', response.status, errorMsg);
      throw new Error(errorMsg);
    }

    const result = await response.json();
    console.log('Order deletion successful:', result);

    return { success: true };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    console.error('Error deleting order:', msg);
    return {
      success: false,
      error: msg || 'Failed to delete order'
    };
  }
}